import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { docs, type DocumentLocation } from "../lib/api";
import { ApiError } from "../lib/http";
import { documentViewPath } from "./documentViewFlow";

/** The document view's address for a located document, at the cited page when there is one. */
export function locatedPath(found: DocumentLocation, page: string | null): string {
  const n = Number.parseInt(page ?? "", 10);
  return documentViewPath(found.package, found.id) + (Number.isFinite(n) && n > 0 ? `?page=${n}` : "");
}

/**
 * /documents/open?path=|key=&package=&page=. A citation knows its document by path or by the
 * register's content key; the document view is keyed by neither. This asks the API where the view
 * is and replaces itself with it, so the back button returns to the citation, not here.
 */
export function OpenDocument() {
  const [params] = useSearchParams();
  const path = params.get("path") ?? undefined;
  const key = params.get("key") ?? undefined;
  const pkg = params.get("package") ?? undefined;
  const [found, setFound] = useState<DocumentLocation | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    setFound(null); setError(null);
    docs.locate({ path, key, package: pkg })
      .then((f) => { if (live) setFound(f); })
      .catch((e) => { if (live) setError(e); });
    return () => { live = false; };
  }, [path, key, pkg]);

  if (found) return <Navigate replace to={locatedPath(found, params.get("page"))} />;
  if (error) {
    const missing = error instanceof ApiError && (error.status === 404 || error.status === 422);
    const refused = error instanceof ApiError && error.status === 403;
    return (
      <div className="page">
        <h2 style={{ fontSize: 26, marginBottom: 6 }}>
          {missing ? "This document is not in a package you can open" : refused ? "This package is outside your access" : "This document could not be opened"}
        </h2>
        <p className="muted">
          {missing ? "The file may have moved since it was cited." : (error as Error).message} <Link to="/documents">All packages</Link>.
        </p>
      </div>
    );
  }
  return <div className="page" role="status"><Loader2 className="muted spin" /> <span className="muted">Finding the document...</span></div>;
}
